
import _ from 'lodash';
import axios from 'axios';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import MuiAlert from '@material-ui/lab/Alert';
import Snackbar from '@material-ui/core/Snackbar';
import {CircularProgress} from "@material-ui/core";

export const DetailContext = React.createContext();


export class DetailProvider extends Component {

  static propTypes = {
    url: PropTypes.string.isRequired,
    onLoaded: PropTypes.func
  };

  constructor(props) {
    super(props);

    this._isMounted = false;
    this._cancelSource = null;

    this.state = {
      url: props.url,
      loading: true,
      item: null,
      loadError: null,
      errors: {},

      snackbarMessage: undefined,
      snackbarShow: false,
      snackbarSeverity: 'info'
    };
  }

  componentDidMount() {
    this._isMounted = true;
    this.loadItem();
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (this._cancelSource) {
      this._cancelSource.cancel();
      this._cancelSource = null;
    }
  }

  getCancelToken() {
    if (this._cancelSource) {
      this._cancelSource.cancel();
    }
    this._cancelSource = axios.CancelToken.source();
    return this._cancelSource.token;
  }

  componentDidUpdate(prevProps) {
    if (this.props.url !== prevProps.url) {
      this.setState({
        url: this.props.url,
        item: null
      }, () => {
        this.loadItem();
      });
    }
  }

  loadItem() {
    const { onLoaded } = this.props;
    this.setState({loading: true}, () => {
      if (!this._isMounted) {
        return;
      }
      axios.get(this.state.url, {cancelToken: this.getCancelToken()})
        .then(res => {
          if (!this._isMounted) {
            return;
          }
          const item = res.data;
          this.setState({
            item: item
          });

          if (onLoaded) {
            onLoaded(item);
          }
        })
        .catch(err => {
          if (axios.isCancel(err) || !this._isMounted) {
            return;
          }
          if (err && err.response) {
            const response = err.response;
            if (response.status === 404) {
              if (response.data && response.data.detail) {
                this.setState({loadError: response.data.detail});
              }
            }
          }
        })
        .then(() => {
          if (this._isMounted) {
            this.setState({loading: false});
          }
        })
    });
  }

  reloadItem = (callback) => {
    axios.get(this.state.url, {cancelToken: this.getCancelToken()})
      .then(res => {
        if (!this._isMounted) {
          return;
        }
        this.setItem(res.data);
        if (callback && typeof callback === 'function') {
          callback(res.data);
        }
      });
  };

  merge = (data) => {
    let { item } = this.state;
    this.setState({
      item: _.merge(item, data)
    })
  }

  patch = (data, config, callback) => {
    axios.patch(this.state.url, data, config)
      .then(res => {
        if (!this._isMounted) {
          return;
        }
        this.merge(res.data);
        if (callback) {
          callback(res.data);
        }
      })
      .catch(error => {
        if (!this._isMounted) {
          return;
        }
        const response = error.response;

        if (response && response.status === 400 && response.data) {
          if (response.data.all_text_errors) {
            this.setState({
              snackbarMessage: response.data.all_text_errors,
              snackbarShow: true,
              snackbarSeverity: 'error'
            });
          }
          else {
            const fields = Object.keys(response.data);
            fields.forEach(field => {
              const errors = response.data[field];
              this.setState({
                snackbarMessage: _.isArray(errors) ? errors[0] : errors,
                snackbarShow: true,
                snackbarSeverity: 'error'
              });
            })
          }
        }
      });
  };

  action = (action, data, config, callback, errorCallback) => {
    axios.post(`${this.state.url}/${action}`, data, config)
      .then(res => {
        if (!this._isMounted) {
          return;
        }
        this.setItem(res.data);
        if (callback) {
          callback(res.data);
        }
      })
      .catch(error => {
        if (!this._isMounted) {
          return;
        }
        const response = error.response;

        const handled = errorCallback ? errorCallback(error) === true : false;
        if (handled) {
          return;
        }

        if (response && response.status === 400 && response.data) {
          if (response.data.all_text_errors) {
            this.setState({
              snackbarMessage: response.data.all_text_errors,
              snackbarShow: true,
              snackbarSeverity: 'error'
            });
          }
          else {
            const fields = Object.keys(response.data);
            fields.forEach(field => {
              const errors = response.data[field];
              this.setState({
                snackbarMessage: _.isArray(errors) ? errors[0] : errors,
                snackbarShow: true,
                snackbarSeverity: 'error'
              });
            })
          }
        }
      });
  };

  update = (data) => {
    axios.put(this.state.url, data)
      .then(res => {
        if (this._isMounted) {
          this.setItem(res.data);
        }
      });
  };

  setItem = (item) => {
    const { onUpdate } = this.props;
    this.setState({item: item});
    if (onUpdate) {
      onUpdate(item);
    }
  };

  setDateValue = (name, value) => {
    let item = this.state.item;
    if (value && moment(value).isValid()) {
      item[name] = moment(value).format('YYYY-MM-DD');
    }
    else {
      item[name] = null;
    }
    this.setState({item: item});
  };

  setValue = (name, value) => {
    let { item } = this.state;
    _.set(item, name, value);
    this.setState({item: item});
  };

  snackbarClose = () => {
    this.setState({
      snackbarShow: false
    })
  };

  setNewUrl = (url) => {
    this.setState({
      url: url,
      item: null
    }, () => {
      this.loadItem();
    })
  };

  render() {
    const { item, snackbarShow, snackbarMessage, snackbarSeverity, loading } = this.state;
    return (
      <DetailContext.Provider value={{
        state: this.state,
        item: this.state.item,
        errors: this.state.errors,
        loading: this.state.loading,
        patch: this.patch,
        action: this.action,
        update: this.update,
        setDateValue: this.setDateValue,
        setValue: this.setValue,
        setItem: this.setItem,
        setNewUrl: this.setNewUrl,
        loadItem: this.loadItem,
        reloadItem: this.reloadItem
      }}>

        <Snackbar
          open={snackbarShow}
          autoHideDuration={3000}
          onClose={() => this.snackbarClose()}
          anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            onClose={() => this.snackbarClose()}
            severity={snackbarSeverity}
          >
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>

        {loading && (
          <div
            style={{
              display: 'grid',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CircularProgress size={80} />
          </div>
        )}

        {item && this.props.children}
      </DetailContext.Provider>
    );
  }
}
